import { DT } from "../../constants.ts";
import { add, norm, type Vec3 } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyStatus, damageBarrier, explode, isEnemy, moveProjectile, playersInRadius } from "../../sim/combat.ts";
import { damageConstruct, spawnConstruct } from "../../sim/constructs.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { ck, fxk, pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, groundInFront, launchProjectile, offWall, ready, secs, trigger, useUlt } from "../kitlib.ts";

export const V = { blockadeCharge: 0, bondTicks: 1 } as const;
export const PK = { BurstAcorn: pk(HeroId.SquirrelyGirl, 0), BlockadeAcorn: pk(HeroId.SquirrelyGirl, 1) } as const;
export const CK = { SquirrelWave: ck(HeroId.SquirrelyGirl, 0) } as const;
export const FX = { Acorn: fxk(HeroId.SquirrelyGirl, 0), Squirrels: fxk(HeroId.SquirrelyGirl, 1) } as const;

const def: HeroDef = {
  id: HeroId.SquirrelyGirl,
  slug: "squirrely-girl",
  name: "Squirrely Girl",
  role: Role.Damage,
  color: "#b87333",
  blurb: "Projectile controller who banks splash acorns, roots divers, and overruns lanes with squirrels.",
  health: 275,
  armor: 0,
  shields: 0,
  radius: 0.38,
  height: 1.65,
  headRadius: 0.22,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 12,
  reloadTime: 1.5,
  ultCost: 2800,
  abilities: {
    [Slot.Primary]: { name: "Boom Acorns", key: "LMB", description: "Lob bouncing acorns that explode in a 3 m radius.", cooldown: 0 },
    [Slot.Secondary]: { name: "Hazel Hold", key: "RMB (hold)", description: "Charge and fire a rooting acorn. More charge means more speed.", cooldown: 8 },
    [Slot.Ability1]: { name: "Tail Spring", key: "Shift", description: "Bounce straight upward from your tail.", cooldown: 8 },
    [Slot.Ability2]: { name: "Mammal Pact", key: "E", description: "Reload and make your next Hazel Hold or Tail Spring ignore cooldown.", cooldown: 12 },
    [Slot.Ultimate]: { name: "Unbeatable Squirrel Surge", key: "Q", description: "Send a damageable squirrel horde racing forward and bouncing off walls.", cooldown: 0 },
  },
  passive: { name: "Scurry Stockpile", description: "Acorns do not crit but splash around corners after bouncing." },
};

function spendOrBond(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, slot: Slot) {
  if (hv(sim.c, eid, V.bondTicks) > 0) {
    setHv(sim.c, eid, V.bondTicks, 0);
    sim.emit({ type: "cast", owner: sim.c.Net.id[eid]!, slot, hero: HeroId.SquirrelyGirl });
    return true;
  }
  if (!ready(sim, eid, slot)) return false;
  consume(sim, eid, slot);
  return true;
}

function acornDir(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number): Vec3 {
  const a = aim(sim, eid);
  return norm(add(a.dir, { x: 0, y: 0.08, z: 0 }));
}

const module: HeroModule = {
  def,
  kit: {
    init() {},
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (hv(c, eid, V.bondTicks) > 0) setHv(c, eid, V.bondTicks, hv(c, eid, V.bondTicks) - 1);
      const prev = c.Player.buttons[eid]!;
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        spawnConstruct(sim, eid, CK.SquirrelWave, groundInFront(sim, eid, input.yaw, 1.5, 0.4), input.yaw, { hp: 300, seconds: 10 });
      }

      if ((pressed & Btn.Ability2) && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        c.Weapon.ammo[eid] = def.ammo;
        c.Weapon.reload[eid] = 0;
        setHv(c, eid, V.bondTicks, secs(5));
        action(sim, eid, ActionKind.Cast, 14);
      }

      if ((pressed & Btn.Ability1) && spendOrBond(sim, eid, Slot.Ability1)) {
        c.Vel.y[eid] = Math.max(c.Vel.y[eid]!, 12.9);
        c.Player.grounded[eid] = 0;
        action(sim, eid, ActionKind.Cast, 12);
      }

      if (input.buttons & Btn.Secondary) setHv(c, eid, V.blockadeCharge, Math.min(1.5, hv(c, eid, V.blockadeCharge) + DT));
      if ((prev & Btn.Secondary) && !(input.buttons & Btn.Secondary)) {
        const charge = hv(c, eid, V.blockadeCharge);
        setHv(c, eid, V.blockadeCharge, 0);
        if (charge > 0.05 && spendOrBond(sim, eid, Slot.Secondary)) {
          const speed = 30 + Math.min(1, charge / 0.7) * 30;
          action(sim, eid, ActionKind.AltFire, 10);
          launchProjectile(sim, eid, PK.BlockadeAcorn, speed, input, { radius: 0.2, seconds: 2.4, gravity: 12, dir: acornDir(sim, eid) });
        }
      }

      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = secs(1 / 1.49);
        action(sim, eid, ActionKind.Fire, 9);
        launchProjectile(sim, eid, PK.BurstAcorn, 80, input, { radius: 0.18, seconds: 2.5, gravity: 16, dir: acornDir(sim, eid) });
      }
    },
  },
  projectiles: {
    [PK.BurstAcorn]: {
      visual: { color: "#f0c27a", size: 0.18, trail: true },
      tick(sim, p) {
        const c = sim.c;
        const owner = c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p);
        if (hit) {
          if (hit.map && hit.normal && c.Projectile.bounces[p]! < 2) {
            const v = { x: c.Vel.x[p]!, y: c.Vel.y[p]!, z: c.Vel.z[p]! };
            const k = 2 * (v.x * hit.normal.x + v.y * hit.normal.y + v.z * hit.normal.z);
            c.Vel.x[p] = (v.x - k * hit.normal.x) * 0.8;
            c.Vel.y[p] = (v.y - k * hit.normal.y) * 0.8;
            c.Vel.z[p] = (v.z - k * hit.normal.z) * 0.8;
            const out = offWall(hit, 0.08);
            c.Pos.x[p] = out.x;
            c.Pos.y[p] = out.y;
            c.Pos.z[p] = out.z;
            c.Projectile.bounces[p] = c.Projectile.bounces[p]! + 1;
          } else {
            explode(sim, owner, hit.pos, { radius: 3, damage: 110, minDamage: 77, fxKind: FX.Acorn });
            sim.destroyEntity(p);
            return;
          }
        }
        if (c.Projectile.ticks[p]! <= 0) {
          explode(sim, owner, sim.posOf(p), { radius: 3, damage: 110, minDamage: 77, fxKind: FX.Acorn });
          sim.destroyEntity(p);
        }
      },
    },
    [PK.BlockadeAcorn]: {
      visual: { color: "#fff2d8", size: 0.22, trail: true },
      tick(sim, p) {
        const c = sim.c;
        const owner = c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p);
        if (hit) {
          if (hit.target !== undefined) {
            applyDamage(sim, hit.target, 50, owner, { pos: hit.pos });
            applyStatus(sim, hit.target, "root", 1.33, { source: owner });
          } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 50, owner);
          else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, 50, owner);
          else sim.emit({ type: "impact", pos: hit.pos, kind: FX.Acorn });
          sim.destroyEntity(p);
          return;
        }
        if (c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
  },
  constructs: {
    [CK.SquirrelWave]: {
      maxHp: 300,
      seconds: 10,
      shape: { kind: "box", hw: 2.5, hd: 1.5, h: 1.75 },
      damageable: true,
      visual: { color: "#b87333", shape: "box" },
      tick(sim, e) {
        const c = sim.c;
        const owner = c.Construct.owner[e]!;
        const yaw = c.Construct.yaw[e]!;
        let fwd = { x: -Math.sin(yaw), y: 0, z: -Math.cos(yaw) };
        const p = sim.posOf(e);
        const hit = sim.physics.raycast(add(p, { x: 0, y: 0.8, z: 0 }), fwd, 1.8);
        if (hit >= 0) {
          c.Construct.yaw[e] = yaw + Math.PI * 0.65;
          fwd = { x: -Math.sin(c.Construct.yaw[e]!), y: 0, z: -Math.cos(c.Construct.yaw[e]!) };
        }
        c.Pos.x[e] = p.x + fwd.x * 9 * DT;
        c.Pos.z[e] = p.z + fwd.z * 9 * DT;
        if (c.Construct.ticks[e]! % 45 === 0) {
          sim.emit({ type: "explosion", pos: add(sim.posOf(e), { x: 0, y: 0.8, z: 0 }), radius: 3, kind: FX.Squirrels });
          for (const { eid: t } of playersInRadius(sim, add(sim.posOf(e), { x: 0, y: 0.8, z: 0 }), 3, (x) => isEnemy(sim, owner, x), false)) {
            applyDamage(sim, t, 150, owner);
            applyStatus(sim, t, "slow", 0.75, { slowPct: 0.3, source: owner });
          }
        }
      },
    },
  },
  fx: {
    [FX.Acorn]: { color: "#f0c27a" },
    [FX.Squirrels]: { color: "#b87333" },
  },
};

export default module;
